import React from 'react';
import { Link, useLocation } from 'react-router-dom';

// Mobile-only persistent bottom navigation.
//
//   ┌──────────────────────────────────────┐
//   │              ╭─────╮                 │
//   │  HOME  VAULT │  ◉  │ EXPLORE ROUTINE │   ← centerItem is a raised FAB
//   │              ╰─────╯                 │
//   └──────────────────────────────────────┘
//
// WHY THE FAB
// CREATE used to be one flat tab of five, first in the row, doing double duty
// as the home screen. Scanning is the one action the whole product depends on —
// 44% of registered accounts have never done it once — so it gets the centre of
// the bar, the largest target, and the only aurum fill in the shell. Everything
// else is navigation; this is the verb.
//
// The remaining items split evenly around it. With an odd number the extra tab
// goes to the left half, which keeps the FAB centred on the 5-slot grid the
// mockup specifies (2 · FAB · 2).
//
// Active tab renders in aurum with a 4px gold dot beneath the label. Includes
// safe-area-inset-bottom for the iOS home indicator.
//
// Props:
//   items: [{ to, label, icon, matchPrefix? }]
//   centerItem: { to, label, icon, matchPrefix? }  raised primary action (optional)

const isActivePath = (pathname, item) =>
  pathname === item.to || Boolean(item.matchPrefix && pathname.startsWith(item.matchPrefix));

const Tab = ({ item, active }) => (
  <li className="flex">
    <Link
      to={item.to}
      aria-current={active ? 'page' : undefined}
      className={[
        'flex-1 flex flex-col items-center justify-center gap-[5px]',
        'font-display uppercase tracking-[0.18em]',
        'transition-colors duration-200 ease-mogged',
        active ? 'text-aurum' : 'text-bone-dim',
      ].join(' ')}
      style={{ fontSize: 9.5 }}
    >
      <span className="inline-flex items-center justify-center w-5 h-5" aria-hidden="true">
        {item.icon}
      </span>
      <span>{item.label}</span>
      <span
        className={['w-1 h-1 rounded-full', active ? 'bg-aurum' : 'bg-transparent'].join(' ')}
        aria-hidden="true"
      />
    </Link>
  </li>
);

// The raised action. It breaks the bar's top edge, so the ring carries the bar's
// own background colour — without it the disc reads as floating over whatever
// content happens to be scrolled underneath.
const CenterTab = ({ item, active }) => (
  <li className="flex items-start justify-center">
    <Link
      to={item.to}
      aria-label={item.label}
      aria-current={active ? 'page' : undefined}
      data-testid="tab-center-action"
      className="relative -mt-[18px] flex flex-col items-center gap-[5px]"
    >
      <span
        className={[
          'flex items-center justify-center w-14 h-14 rounded-full',
          'border transition-colors duration-200 ease-mogged',
          active
            ? 'bg-aurum text-obsidian border-aurum'
            : 'bg-[#0a0a09] text-aurum border-aurum/55',
        ].join(' ')}
        style={{ boxShadow: '0 0 0 5px #0a0a09' }}
        aria-hidden="true"
      >
        {item.icon}
      </span>
      <span
        className="font-display uppercase tracking-[0.18em] text-bone-dim"
        style={{ fontSize: 9.5 }}
      >
        {item.label}
      </span>
    </Link>
  </li>
);

const BottomTabBar = ({ items, centerItem = null }) => {
  const location = useLocation();

  const columns = centerItem ? items.length + 1 : items.length;
  const splitAt = centerItem ? Math.ceil(items.length / 2) : items.length;
  const left = items.slice(0, splitAt);
  const right = items.slice(splitAt);

  const renderTab = (item) => (
    <Tab key={item.to} item={item} active={isActivePath(location.pathname, item)} />
  );

  return (
    <nav
      className="lg:hidden fixed inset-x-0 bottom-0 bg-[#0a0a09]/95 backdrop-blur-md border-t border-hairline-soft pb-safe-bottom z-40"
      style={{ height: 'calc(72px + env(safe-area-inset-bottom))' }}
      aria-label="primary navigation"
    >
      <ul
        className="grid h-[72px] m-0 p-0 list-none"
        style={{ gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))` }}
      >
        {left.map(renderTab)}
        {centerItem && (
          <CenterTab
            key={centerItem.to}
            item={centerItem}
            active={isActivePath(location.pathname, centerItem)}
          />
        )}
        {right.map(renderTab)}
      </ul>
    </nav>
  );
};

export default BottomTabBar;
